import {useEffect, useMemo, useState} from "react";
import {useAtomValue, useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";
import {Button, Grid, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Dialog} from "../base/Dialog";
import {Frame} from "../base/Frame";
import {CompanyHierarchyView, RenderCompanyItemFn} from "../companies/CompanyHierarchyView";
import {HighlightedText} from "../base/HighlightedText";
import {demoCompanyLoadable} from "../../store/demoCompanyStore";
import {CompanyEditDialog} from "./CompanyEditDialog";
import {authenticatedUserAtom} from "../../store/authenticatedUserStore";
import {CompanyIcon} from "../icons/CompanyIcon";
import {useNavigate, useParams} from "react-router-dom";
import {useUserCompanies} from "../../hooks/companiesHooks";
import {useQueryClient} from "@tanstack/react-query";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean, companyId?: string) => void;
}

export const CompanySwitchDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [createCompanyDialogOpened, setCreateCompanyDialogOpened] = useState<boolean>(false);
    const userCompanies = useUserCompanies({includeChildCompanies: true});
    const {companyId} = useParams();
    const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
    const demoCompanyState = useAtomValue(demoCompanyLoadable);
    const companies = useMemo(() => {
        return userCompanies.isSuccess && demoCompanyState.state === "hasData"
            ? [...userCompanies.data, demoCompanyState.data]
            : [];
    }, [userCompanies.isSuccess, userCompanies.data, demoCompanyState]);
    const refreshAuthenticatedUser = useSetAtom(authenticatedUserAtom);

    // TODO: Implement error handling. It should be hook taking Resources as input and providing
    // individual reactive error object as output for error notification
    const error = undefined;

    useEffect(() => {
        if (companyId) {
            setSelectedCompanyId(companyId);
        }
    }, [companyId]);

    const onSubmit = () => {
        const chosenCompany = userCompanies.isSuccess
            ? userCompanies?.data?.find((comp) => comp.id === selectedCompanyId)
            : null;
        onClose(true, selectedCompanyId ?? undefined);
        if (selectedCompanyId) {
            if (chosenCompany?.includeChildCompanies) {
                const params = new URLSearchParams(location.search);
                params.set("parentCompany", selectedCompanyId);
                navigate({
                    pathname: location.pathname,
                    search: params.toString(),
                });
            }
            navigate({
                pathname: location.pathname,
            });
        }
    };

    const onNewCompanyDialogClose = (isSuccessful: boolean) => {
        setCreateCompanyDialogOpened(false);
        if (isSuccessful) {
            void queryClient.invalidateQueries({queryKey: ["userCompanies"]});
            refreshAuthenticatedUser();
        }
    };

    return (
        <Dialog
            width="50rem"
            enableSubmit
            submitTitle={t("dialogs.company_switch.switch_button")}
            title={t("dialogs.company_switch.title")}
            titleIcon={<CompanyIcon sx={{stroke: "#404DB8", width: "1.25rem", height: "1.25rem"}} />}
            open={open}
            error={error}
            onClose={() => onClose(false)}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={onSubmit}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Frame>
                        <Stack direction="column" gap="1rem">
                            <Stack
                                direction="row"
                                marginY="-0.25rem"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Typography fontSize={16} textTransform="uppercase">
                                    {t("dialogs.company_switch.managed", {length: companies.length})}
                                </Typography>
                                <Button
                                    sx={{whiteSpace: "nowrap"}}
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateCompanyDialogOpened(true)}
                                >
                                    {t("companies.header.create")}
                                </Button>
                            </Stack>
                            <CompanyHierarchyView
                                companies={companies}
                                isLoading={userCompanies.isLoading || demoCompanyState.state === "loading"}
                                selectedCompanyId={selectedCompanyId}
                                onCompanySelect={setSelectedCompanyId}
                                renderItem={renderCompanyItem}
                            />
                        </Stack>
                    </Frame>
                </Grid>
            </Grid>

            {createCompanyDialogOpened && (
                <CompanyEditDialog mode="create" open={createCompanyDialogOpened} onClose={onNewCompanyDialogClose} />
            )}
        </Dialog>
    );
};

const renderCompanyItem: RenderCompanyItemFn = ({company, searchFilter, selectedCompanyId}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem" width="100%">
        <Stack direction="row" alignItems="center" gap="0.6rem">
            <CompanyIcon
                sx={{
                    stroke: "#404DB8",
                    width: "1.125rem",
                    height: "1.125rem",
                }}
            />
            <Typography fontSize={14} textOverflow="ellipsis" noWrap>
                <HighlightedText text={company.properties.name} highlight={searchFilter}></HighlightedText>
            </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap="1rem">
            <Stack direction="row" textAlign="right" gap="0.5rem">
                <Typography
                    fontSize={14}
                    textTransform="uppercase"
                    color={company.id === selectedCompanyId ? "white" : "#545A6E"}
                    width="6rem"
                    maxWidth="6rem"
                >
                    {company.aggregatedProps.totalFields} Fields
                </Typography>
            </Stack>
            {/* <Stack direction="row" alignItems="center" gap="0.5rem">
                <CircleIcon
                    sx={{
                        color: "#EF6154",
                        width: "1rem",
                        height: "1rem",
                    }}
                />
                <Typography
                    fontSize={14}
                    textTransform="uppercase"
                    color={company.id === selectedCompanyId ? "white" : "#545A6E"}
                    width="6rem"
                    maxWidth="6rem"
                >
                    10 Alerts
                </Typography>
            </Stack> */}
        </Stack>
    </Stack>
);
